import { Link } from 'react-router-dom';
import { PiWarningCircleBold } from 'react-icons/pi';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { SellerDefaultedInstallments as DefaultedPayload } from '@v2/pages/commissioning/types';
import { fmtBrl } from './homeFormat';

type Props = {
    data?: DefaultedPayload | null;
};

export default function SellerDefaultedInstallments({ data }: Props) {
    const { t, locale } = useI18n();
    const dateLocale = locale === 'pt_BR' ? 'pt-BR' : locale === 'zh_CN' ? 'zh-CN' : locale === 'es' ? 'es' : 'en-US';

    const items = data?.items ?? [];
    const count = data?.count ?? 0;
    const blocked = data?.commission_blocked_total ?? 0;
    const hasItems = count > 0;

    const fmtDate = (iso: string | null) => {
        if (!iso) return '—';
        const d = new Date(`${iso}T12:00:00`);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString(dateLocale, { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
        <section
            className={cn(
                'rounded-2xl border p-4',
                hasItems
                    ? 'border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/20'
                    : 'border-dashed border-gray-300 bg-gray-50/80 dark:border-zinc-600 dark:bg-zinc-900/80',
            )}
            aria-label={t('v2.my_commission.defaulted_title')}
            data-testid="seller-defaulted-installments"
        >
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="flex items-center gap-1.5">
                        <PiWarningCircleBold className={cn(
                            'size-4',
                            hasItems ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-zinc-500',
                        )} />
                        <h2 className={cn(
                            'text-sm font-bold uppercase tracking-wide',
                            hasItems ? 'text-red-900 dark:text-red-100' : 'text-gray-700 dark:text-zinc-200',
                        )}>
                            {t('v2.my_commission.defaulted_title')}
                        </h2>
                    </div>
                    <p className={cn(
                        'mt-1 text-xs',
                        hasItems ? 'text-red-700/80 dark:text-red-200/70' : 'text-gray-500 dark:text-zinc-400',
                    )}>
                        {hasItems
                            ? t('v2.my_commission.defaulted_hint', {
                                count: String(count),
                                amount: fmtBrl(blocked),
                            })
                            : t('v2.my_commission.defaulted_empty')}
                    </p>
                </div>
                {hasItems && (
                    <div className="text-right">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-red-600/80 dark:text-red-300/80">
                            {t('v2.my_commission.defaulted_blocked_label')}
                        </p>
                        <p className="text-lg font-bold tabular-nums text-red-800 dark:text-red-200">
                            {fmtBrl(blocked)}
                        </p>
                    </div>
                )}
            </div>

            {hasItems && (
                <ul className="space-y-2">
                    {items.slice(0, 8).map((row) => {
                        const isDefaulted = row.status === 'defaulted';
                        return (
                            <li
                                key={row.installment_id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-100 bg-white/80 px-3 py-2.5 dark:border-red-900/50 dark:bg-zinc-950/40"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
                                        <span className={cn(
                                            'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                                            isDefaulted
                                                ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
                                                : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
                                        )}>
                                            {isDefaulted
                                                ? t('v2.my_commission.defaulted_badge_defaulted')
                                                : t('v2.my_commission.defaulted_badge_overdue')}
                                        </span>
                                        <Link
                                            to={`/ploomes-deals/${row.deal_id}?tab=commission`}
                                            className="truncate text-sm font-semibold text-gray-900 hover:text-orange-700 dark:text-zinc-100 dark:hover:text-orange-300"
                                        >
                                            {row.display_sale_number || row.deal_number || `#${row.deal_id}`}
                                            {row.deal_title ? ` · ${row.deal_title}` : ''}
                                        </Link>
                                    </div>
                                    <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
                                        {t('v2.my_commission.defaulted_installment', {
                                            n: String(row.installment_number),
                                            total: String(row.total_installments || '—'),
                                        })}
                                        {row.client_name ? ` · ${row.client_name}` : ''}
                                        {' · '}
                                        {t('v2.my_commission.defaulted_due', { date: fmtDate(row.due_date) })}
                                        {row.days_overdue != null && row.days_overdue > 0
                                            ? ` · ${t('v2.my_commission.defaulted_days', { days: String(row.days_overdue) })}`
                                            : ''}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold tabular-nums text-red-800 dark:text-red-200">
                                        {fmtBrl(row.commission_amount)}
                                    </p>
                                    <p className="text-[10px] text-gray-400">
                                        {t('v2.my_commission.defaulted_parcel_amount', {
                                            amount: fmtBrl(row.installment_amount),
                                        })}
                                    </p>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {hasItems && items.length > 8 && (
                <p className="mt-3 text-center text-xs text-red-700/70 dark:text-red-200/60">
                    {t('v2.my_commission.defaulted_more', { count: String(items.length - 8) })}
                </p>
            )}
        </section>
    );
}
